/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { EntityVersion, ID, WithDirty, WithEntityState, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock } from '@gh/core-data';
import { SalesFolderStatus, SiteAddressInfo } from '../leads';
import { ScheduleType } from '../schedule-templates';
import { UserDetailsShortDto } from '../users';
import { FocusedCell, GridColumn } from '../../core-ui-grid';
import { createArrayHelper } from '@gh/common-shared';

export const DatesFilter = createEnum('DATES_FILTERS', {
  Sale:           1,
  ContractSigned: 2,
  PermitApproval: 3,
  Slab:           4,
  FinalClaim:     5,
  Handover:       6,
  NotProceeding:  7,
  ReportedSlab:   8,
  ReportedSale:   9,
});

export type DatesFilter = EnumSet<typeof DatesFilter>;

export const CMDatesFilter = createEnum('CM_DATES_FILTERS', {
  Sale:           1,
  ContractSigned: 2,
  PermitApproval: 3,
  Slab:           4,
  FinalClaim:     5,
  Handover:       6,
  NotProceeding:  7,
  ReportedSlab:   8,
  ReportedSale:   9,
});

export type CMDatesFilter = EnumSet<typeof CMDatesFilter>;

export const ContractType = createEnum('CONTRACT_TYPE', {
  Presold:              'PRESOLD',
  Contract:             'CONTRACT',
  ModelHome:            'DISPLAY_HOME',
  FranchiseeOwnHome:    'FRANCHISEE_OWN_HOME',
  SpecialCircumstances: 'SPECIAL_CIRCUMSTANCES',
  Speculative:          'SPECULATIVE',
  MinorWorks:           'MINOR_WORKS',
});
export type ContractType = EnumSet<typeof ContractType>;

export const JobStatus = createEnum('JOB_STATUS', {
  Dpp:           'DPP',
  Finalized:     'FINALIZED',
  NotProceeding: 'NOT_PROCEEDING',
  Active:        'ACTIVE',
});
export type JobStatus = EnumSet<typeof JobStatus>;

export type JobListItemDto = WithVersion<{
  id: ID;
  jobNumber: string;
  contactName: string;
  siteAddress: string;
  contractType: string;
  contractTypeValue: ContractType;
  status: string;
  statusValue: JobStatus;
  isCsExist: boolean;
  isSpecExist: boolean;
  isDpp: boolean;
  isFinalized: boolean;
  isNotProceeding: boolean;
  housePlanCost: number;
  housePlanCode: string;
  housePlanName: string;
  priceBookName: string;
  specProducedDate: Date;
  masterZoneId?: ID;
  officeId?: ID;
  finalizedDate: Date;
  totalOfficeFee: number;
  totalOfficeVariationFee: number;
  officePaymentDue: number;
  totalMasterFee: number;
  totalMasterVariationFee: number;
  masterPaymentDue: number;
  reportedSaleDate?: Date;
  reportedSlabDate?: Date;
  saleDateReported?: Date;
  slabDateReported?: Date;
  comments: string;
  unitsCount: number;
}>;

export type JobListFilterDto = {
  searchString?: string;
  showFinalized?: boolean;
  showNotProceeding?: boolean;
  showDpp?: boolean;
  showActive?: boolean;
  isHandoverDateExist?: boolean;
  isDrawRuleExist?: boolean;
  isAuthToCommenceExist?: boolean;
  notEmpty?: boolean;
  isSpecExist?: boolean;
  isSlabExist?: boolean;
  isCsExist?: boolean;
  scheduleNotExist?: ScheduleType;
  autocomplete?: true;
  masterZoneId?: string;
  officeId?: string;
  dateFrom?: string;
  dateTo?: string;
  dateType?: DatesFilter;
  filterType?: string;
};

export type CMJobListFilterDto = {
  searchString?: string;
  showFinalized?: boolean;
  showNotProceeding?: boolean;
  showDpp?: boolean;
  isHandoverDateExist?: boolean;
  isDrawRuleExist?: boolean;
  isAuthToCommenceExist?: boolean;
  notEmpty?: boolean;
  isSpecExist?: boolean;
  isSlabExist?: boolean;
  isCsExist?: boolean;
  scheduleNotExist?: ScheduleType;
  autocomplete?: true;
  masterZoneId?: string;
  officeId?: string;
  dateFrom?: string;
  dateTo?: string;
  dateType?: CMDatesFilter;
};

export type JobBaseDesignFilterDto = {
  showDeleted?: boolean;
  searchString?: string;
};

export type JobBaseDesignListItemDto = {
  id: ID;
  name: string;
  version: EntityVersion;
  isActive: boolean;
};

export type JobQuickCreateRequestDto = SiteAddressInfo & {
  contactId: ID;
  salespersonId: ID;
  owner1: string;
  owner2: string;
  owner3: string;
  siteName: string;
  postCode: string;
  mapReference: string;
  reference: string;
  priceBookId: ID;
  housePlanId: ID;
  housePlanName: string;
  baseDesignId: ID;
  jobMemo: string;
  unitsCount: number;

  // prefilled from labels
  salesFolderName: string;
  quotationDescription: string;

  version: EntityVersion; // contact version
  createdLocal: Date; // needed for sales folder creation
};

export type JobQuickCreateResponseDto = {
  jobId: ID;
  jobNumber: number;
};

export type JobDppDetailsDto = WithOptimisticLock<{
  id: ID;
  jobNumber: string;
  salesFolderName: string;
  salesFolderId: ID;
  salesFolderAddedDate: Date;
  salesFolderStatus: SalesFolderStatus;
  jobAdmin?: UserDetailsShortDto;
  isJobAdminActive: boolean;
  customerName: string;
  contactId: ID;
  contactEmail: string;
  basicEmail: string;
  contactPhone: string;
  salesPersonName: string;
  address: string;
  contractAmount: number;
  saleDate: Date;
  depositDate: Date;
  notProceedingDate?: Date;
  memo: string;
  version: EntityVersion;
  isNotProceeding: boolean;
  siteBlockNumber: string;
  siteCity: string;
  siteLotNumber: string;
  siteMapReference: string;
  siteReference: string;
  siteName: string;
  sitePostcode: string;
  siteState: string;
  siteStreet: string;
  siteStreetNumber: string;
  siteSuburb: string;
  isSpecExist: boolean;
  isFinalized: boolean;
  jobDocsFolderId: ID;
  countyId: ID;
  countyName: string;
  unitsCount: number;
  county: any;
}>;

export type JobUpdateDPPDto = WithOptimisticLock<{
  id: ID;
  contractAmount: number;
  memo: string;
  jobAdminId?: ID;
  siteName: string;
  siteLotNumber: string;
  siteMapReference: string;
  siteBlockNumber: string;
  siteStreet: string;
  siteStreetNumber: string;
  siteSuburb: string;
  sitePostcode: string;
  siteCity: string;
  siteState: string;
  countyId: ID;
}>;

export type JobRedoDto = {
  jobId: ID;
  housePlanId: ID;
  priceBookId: ID;
  version: EntityVersion;
  confirmations?: { skipConstructionScheduleCheck: boolean };
};

export type JobBasicInfoDto = {
  jobId: ID;
  jobNumber: number;
  contactName: string;
  basicEmail: string;
  isNotProceeding: boolean;
  isFinalized: boolean;
  isQuickJob: boolean;
  salesFolderLink: string;
  jobDocsFolderId: ID;
  jobDocsLink: string;
  contactId: ID;
  contactEmail: string;
  contactPhone: string;
  useFacility: boolean;
  isCsExist: boolean;
  isSpecExist: boolean;
  salespersonId?: ID;
  version: EntityVersion;
};

export type JobChangeClientDto = {
  version: EntityVersion;
  contactId: ID;
  originalContactId: ID;
};

export type JobBillingExcludedItemDto = {
  jobId: ID;
  itemId: string;
  isItemExclude: boolean;
  excludeItemCode: string;
  excludeItemDesc: string;
  excludeItemExtVarDollar: number;
  excludeItemExtNotVarDollar: number;
};

export type JobBillingDto = {
  amount: number;
  amountWithGst: number;
  comments: string;
  contactName: string;
  isCorporateConfirmedNotSlab: boolean;
  franchiseeFee: number;
  franchiseeVariationFee: number;
  handoverDate: Date;
  isDpp: boolean;
  isNotProceeding: boolean;
  isFinalised: boolean;
  isPeriodClosed: boolean;
  jobId: ID;
  jobNumber: number;
  isMasterConfirmedNotSlab: boolean;
  masterFee: number;
  masterLastPaymentDate: Date;
  masterPaidAmount: number;
  masterPaidToDate: number;
  masterPaymentDue: number;
  masterVariationFee: number;
  officeId: ID;
  officeLastPaymentDate: Date;
  officePaidAmount: number;
  officePaidToDate: number;
  officePaymentDue: number;
  reportedSlabDate: Date;
  reportedSaleDate: Date;
  saleDate: Date;
  slabDate: Date;
  totalMasterFee: number;
  totalMasterVariationFee: number;
  totalOfficeFee: number;
  totalOfficeVariationFee: number;
  variationsAmount: number;
  variationsAmountWithGst: number;
  type: string;
  items: JobBillingExcludedItemDto[];
  excludeItemTotalExtVarDollar: number;
  excludeItemTotalExtNotVarDollar: number;
  unitsCount: number;
};

export type JobBillingWorksheet = {
  items: JobBillingWorksheetItem[];
  focusedCell?: FocusedCell;
};
export type JobBillingWorksheetItem  = WithEntityState<JobBillingWorksheetItemDto>;
export type JobBillingWorksheetItemDto = WithDirty<WithVersion<WithOptimisticLock<{
  id: ID;
  jobNumber: string;
  officeId: ID;
  contractType: string;
  contractTypeValue: string;
  contractAmount: number;
  totalOfficeFee: number;
  totalOfficeVariationFee: number;
  officePaymentDue: number;
  officePaidToDate: number;
  officeLastPaymentDate?: Date;
  officePaymentDate?: Date;
  officePaidAmount: number;
  totalMasterFee: number;
  totalMasterVariationFee: number;
  masterPaymentDue: number;
  masterPaidToDate: number;
  masterLastPaymentDate?: Date;
  masterPaymentDate?: Date;
  masterPaidAmount: number;
  comments: string;
  isMasterDateDirty?: boolean;
  isOfficeDateDirty?: boolean;
  isMasterAmountDirty?: boolean;
  isOfficeAmountDirty?: boolean;
}>>>;

export type JobBillingWorksheetExportDto = {
  gridLayout: {
    columns: GridColumn[];
  };
  jobList: JobBillingWorksheetItem[];
};

export const CM_WORKSHEET_INIT: JobBillingWorksheet = {
  items: [],
};

export const getWorksheetId = (job: JobBillingWorksheetItem) => job.id;
export const jobBillingWorksheetArr = createArrayHelper(getWorksheetId);
